import resolve from './resolve.js';

/**
 * Advanced utility for extracting and transforming data using path notation
 * Combines the path notation style of resolve() with the data transformation capabilities of extractDataFromPath()
 * 
 * @param {string} path - Path using resolve-style notation (e.g., "orders[].lines[].itemId", "warehouse.sections[].name")
 * @param {Object} data - The data object to extract from
 * @param {Object} props - Configuration object with data2Copy and data2add options
 * @returns {*} Extracted and transformed data
 */
function extract(path, data, props = {}) {
    const { data2Copy = [], data2add = [] } = props;

    // Handle null/undefined inputs
    if (!path || data === null || data === undefined) {
        return undefined;
    }

    // If no transformation props are provided, just use resolve directly
    if (data2Copy.length === 0 && data2add.length === 0) {
        return resolve(path, data);
    }

    // Parse additional fields into segments
    const parsedAdditionalFields = data2Copy.map(field => {
        // Handle both string format ('lines.itemName') and object format ({'docId': 'id'})
        if (typeof field === 'string') {
            // Use resolve to get the value from the original data
            // Handle [i] syntax in field names - extract the base field name
            let fieldName = field.split('.').pop(); // Use the last part as field name
            if (fieldName.includes('[i]')) {
                fieldName = fieldName.replace('[i]', ''); // Remove [i] from field name
            }
            return {
                fieldName,
                sourcePath: field,
                type: 'resolve' // Use resolve to get the value
            };
        } else if (typeof field === 'object' && field !== null) {
            const [targetField, sourceField] = Object.entries(field)[0];
            return {
                targetField,
                sourceField,
                type: 'state' // State-based field access (can also handle [i] syntax)
            };
        }
        throw new Error('Invalid field format in data2Copy. Must be string or object.');
    });

    // Process data2add fields
    const staticFields = {};
    data2add.forEach(field => {
        const entries = Object.entries(field);
        if (entries.length > 0) {
            const [key, value] = entries[0];
            staticFields[key] = value;
        }
    });

    // Use resolve to get the base result
    const baseResult = resolve(path, data);

    // If no result from resolve, return undefined
    if (baseResult === undefined) {
        return undefined;
    }

    // Apply transformations to the result
    return applyTransformations(baseResult, data, parsedAdditionalFields, staticFields, path);
}

/**
 * Apply data transformations (data2Copy and data2add) to the resolved result
 */
function applyTransformations(result, originalData, parsedAdditionalFields, staticFields, originalPath) {
    // If result is an array, transform each item with its index context
    if (Array.isArray(result)) {
        return result.map((item, index) => transformItem(item, originalData, parsedAdditionalFields, staticFields, originalPath, index));
    }
    
    // If result is a single item, transform it
    return transformItem(result, originalData, parsedAdditionalFields, staticFields, originalPath, 0);
}

/**
 * Transform a single item by applying data2Copy and data2add
 */
function transformItem(item, originalData, parsedAdditionalFields, staticFields, originalPath, itemIndex = 0) {
    // For primitive values, just return as-is (can't add fields to primitives)
    if (typeof item !== 'object' || item === null) {
        return item;
    }

    // Start with the original item
    let transformedItem = { ...item };

    // Apply data2Copy transformations
    parsedAdditionalFields.forEach(field => {
        if (field.type === 'resolve') {
            // Check if this needs parent index substitution
            let sourcePath = field.sourcePath;
            if (sourcePath.includes('[i]')) {
                // Calculate the parent index for this item
                const parentIndex = calculateParentIndex(originalData, originalPath, itemIndex);
                sourcePath = sourcePath.replace('[i]', `[${parentIndex}]`);
            }
            
            // Use resolve to get value from original data using the processed path
            const sourceValue = resolve(sourcePath, originalData);
            if (sourceValue !== undefined) {
                transformedItem[field.fieldName] = sourceValue;
            }
        } else if (field.type === 'state') {
            // State-based field access from the original data (can also handle [i] syntax)
            let sourceField = field.sourceField;
            if (sourceField.includes('[i]')) {
                // Calculate the parent index for this item
                const parentIndex = calculateParentIndex(originalData, originalPath, itemIndex);
                sourceField = sourceField.replace('[i]', `[${parentIndex}]`);
            }
            
            const sourceValue = resolve(sourceField, originalData);
            if (sourceValue !== undefined) {
                transformedItem[field.targetField] = sourceValue;
            }
        }
    });

    // Apply data2add static fields
    Object.assign(transformedItem, staticFields);

    return transformedItem;
}

/**
 * Calculate which parent array item this child item belongs to
 * For paths like "companies[].employees[]", determine which company each employee belongs to
 */
function calculateParentIndex(originalData, targetPath, itemIndex) {
    // Parse the target path to understand the hierarchy
    // e.g., "companies[].employees[]" -> ["companies", "employees"]
    const pathSegments = targetPath.split('[]').filter(segment => segment);
    
    if (pathSegments.length < 2) {
        return 0; // No parent-child relationship
    }
    
    const parentArrayPath = pathSegments[0]; // "companies"
    const childArrayPath = pathSegments[1].startsWith('.') ? 
        pathSegments[1].substring(1) : pathSegments[1]; // "employees"
    
    // Get the parent array
    const parentArray = resolve(parentArrayPath, originalData);
    if (!Array.isArray(parentArray)) {
        return 0;
    }
    
    // Calculate which parent this item belongs to based on cumulative child counts
    let currentIndex = 0;
    for (let parentIndex = 0; parentIndex < parentArray.length; parentIndex++) {
        const parent = parentArray[parentIndex];
        const childArray = resolve(childArrayPath, parent);
        
        if (Array.isArray(childArray)) {
            const childArrayLength = childArray.length;
            
            // Check if our item index falls within this parent's children
            if (itemIndex >= currentIndex && itemIndex < currentIndex + childArrayLength) {
                return parentIndex;
            }
            
            currentIndex += childArrayLength;
        }
    }
    
    return 0; // Fallback
}

export default extract;
